import type { IDistrict, IRegion } from "@agrigate/location-types";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import { ensureNN } from "@agrigate/ts-utils/lib/coercions";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { Combobox, ComboboxRootProps } from "@chakra-ui/react";
import { compact } from "lodash";
import { useEffect, useMemo } from "react";
import ComboboxSelect from "./ComboSelect";
import { getRPC } from "./utils/rpc";

export interface District {
    districtId: string;
    districtName: string;
}

export default function DistrictDropDown(p: {
    name?: string;
    label?: string;
    regionId?: MaybeN<string>;
    districtId?: MaybeN<string>;
    defaultValues?: MaybeN<string[]>;
    onRegionInit?: (region: IRegion) => void;
    onChange?: (
        districtId?: string,
        districtName?: string,
        regionId?: string,
    ) => void;
    autofetchInitial?: MaybeN<boolean>;
    allowedDistrictIds?: string[];
    disabled?: boolean;
    width?: string;
    allowCreation?: boolean;
    placement?: ComboboxRootProps["placeContent"];
    allowFetchAllDistricts?: boolean;
    omitdistrictIds?: MaybeN<string[]>;
    setDefaultValues?: (districts: District[]) => void;
    triggerFetchAll?: boolean;
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}) {
    const fetchDistrictsT = useAsyncTask(
        async () => {
            if (!p.regionId && !p.allowFetchAllDistricts) return;
            return getRPC().location.findAllDistricts({
                filters: {
                    regionId: { eq: p.regionId },
                },
            });
        },
        {
            deps: [p.regionId],
            autoRun: true,
        },
    );

    const districts: MaybeN<IDistrict[]> = useMemo(() => {
        const allDistricts = fetchDistrictsT.data?.districts;
        if (!allDistricts) return allDistricts;
        if (!p.allowedDistrictIds?.length) {
            return allDistricts;
        }
        return allDistricts.filter(d => p.allowedDistrictIds!.includes(d.id));
    }, [fetchDistrictsT.data, p.allowedDistrictIds]);

    const districtOptions = useMemo(() => {
        if (!districts) return [];
        const options: {
            value: string;
            label: string;
            item: MaybeN<IDistrict>;
        }[] = districts
            .filter(district => !p.defaultValues?.includes(district.id))
            .map(district => ({
                value: district.id,
                label: p.isDesktop ? district.name : district.name.slice(0, 30),
                item: district,
            }));
        if (!p.allowCreation || !p.regionId) return options;
        return options.concat({
            value: "$new",
            label: "New District",
            item: null,
        });
    }, [districts, p.allowCreation, p.defaultValues]);

    useEffect(() => {
        if (p.triggerFetchAll && districts) {
            p.setDefaultValues?.(
                districts.map(district => ({
                    districtId: district.id,
                    districtName: district.name,
                })),
            );
        }
    }, [p.triggerFetchAll]);

    useEffect(() => {
        if (p.defaultValues && districts?.length) {
            const selectedDistricts = p.defaultValues.map(districtId => {
                const district = districts?.find(_ => _.id === districtId);
                if (district)
                    return {
                        districtId: district?.id,
                        districtName: district?.name,
                    };
                else return null;
            });
            p.setDefaultValues?.(compact(selectedDistricts));
        }
    }, [districts]);

    return (
        <ComboboxSelect
            label={p.label}
            placeholder="Select District"
            portalled={p.portalled ?? true}
            comboRootProps={{
                disabled:
                    p.disabled || (!p.regionId && !p.allowFetchAllDistricts),
                ...p.comboRootProps,
            }}
            value={p.districtId ? [p.districtId] : []}
            items={districtOptions ?? []}
            onChange={async selectedIds => {
                const selectedId = selectedIds[0];
                let district: MaybeN<IDistrict>;

                if (selectedId === "$new") {
                    const name = prompt("Enter district name")?.trim();
                    if (!name) return;

                    ({ district } = await getRPC().location.addDistrict({
                        name,
                        regionId: ensureNN(p.regionId),
                    }));
                } else {
                    district = selectedId
                        ? districts?.find(d => d.id === selectedId)
                        : null;
                }

                p.onChange?.(
                    district?.id,
                    district?.name,
                    district?.regionId ?? undefined,
                );
                fetchDistrictsT.trigger();
            }}
            width={p.width ?? "20%"}
            placement={p.placement}
            comboBoxProps={{ ...p.comboBoxProps }}
            comboInputProps={{ ...p.comboInputProps }}
        />
    );
}
